import { useQuery } from '@apollo/client/react';
import { useCallback, useMemo, useState } from 'react';
import {
  fetchCallReasons,
  FetchedCallReason,
  getCallReasonsQuery,
} from '../queries/callReasons';

export const useCallReasons = (tenantId?: string, skip: boolean = false) => {
  const { data, loading, error, refetch } = useQuery(getCallReasonsQuery, {
    // The default value of '0' is to satiate the typechecker. The 'skip' value
    // of !tenantId ensures we don't actually send requests to the BFF when
    // there is no tenant ID.
    variables: { tenantId: tenantId ?? '0' },
    notifyOnNetworkStatusChange: true,
    skip: !tenantId || skip,
  });

  // will extract 1000 reasons which should be "all"
  const callReasons = useMemo(
    () => data?.callReasons?.items ?? [],
    [data?.callReasons?.items],
  );

  return { callReasons, loading, error, refetch };
};

export const useCallReasonsSearch = () => {
  const [callReasons, setCallReasons] = useState<FetchedCallReason[]>([]);
  const [callReasonsLoading, setCallReasonsLoading] = useState(false);
  const [callReasonsError, setCallReasonsError] = useState<string | null>(null);

  const fetch = useCallback(
    (tenantId: string, take?: number, skip?: number) => {
      setCallReasonsLoading(true);
      setCallReasonsError(null);

      fetchCallReasons(tenantId, { take, skip })
        .then((items) => {
          setCallReasons(items);
        })
        .catch((err: string) => {
          setCallReasonsError(err.toString());
        })
        .finally(() => {
          setCallReasonsLoading(false);
        });
    },
    [],
  );

  return useMemo(
    () => ({
      callReasons,
      callReasonsLoading,
      callReasonsError,
      callReasonsSearch: fetch,
    }),
    [callReasons, callReasonsLoading, callReasonsError, fetch],
  );
};

export default useCallReasons;
